import { Controller } from '@hotwired/stimulus'
import {
  rules, getRulesByCategory, getWordReplacementPairs, getRuleById,
  getDefaultEnabledIds, loadPreferences, savePreferences, clearPreferences
} from '../utils/text_formatter'

/**
 * 格式調整器設定面板的 Stimulus controller。
 *
 * 管理所有 toggle/radio UI 與偏好持久化，
 * 每次偏好變更後 dispatch 'formatter-settings:changed' custom event，
 * 讓外層 controller（如 text-formatter）監聽並重新格式化。
 *
 * 此 controller 由 shared partial `_formatter_settings.html.erb` 使用，
 * 可同時嵌入格式調整器頁面和 Notion 頁面。
 */

// 括號規則由 radio 獨立控制，不受 category toggle 影響
const PARENS_RULE_IDS = new Set(['punct-half-to-full-parens', 'punct-full-to-half-parens'])

export default class extends Controller {
  static targets = [
    'categoryToggle', 'ruleToggle', 'parensRadio',
    'ruleExample', 'wordPairsContainer', 'wordPairsToggle',
    'postFixContainer', 'postFixToggle',
    'sidebar', 'sidebarToggle', 'sidebarArrow'
  ]

  connect() {
    this.enabledIds = loadPreferences()
    this._syncAllToggles()
    this._renderExamples()
    this._renderWordPairs()
  }

  /** 切換整個類別的開關，同步更新子規則 checkbox 狀態 */
  toggleCategory(event) {
    const checkbox = event.target
    const category = checkbox.dataset.category
    const enabled = checkbox.checked

    // 排除括號規則（由 radio 獨立控制）
    const ruleIds = getRulesByCategory(category)
      .filter(r => !PARENS_RULE_IDS.has(r.id))
      .map(r => r.id)

    ruleIds.forEach(id => {
      if (enabled) {
        this.enabledIds.add(id)
      } else {
        this.enabledIds.delete(id)
      }
    })

    // 同步子規則 checkbox（如果有展開的話）
    this.ruleToggleTargets
      .filter(el => el.dataset.category === category)
      .forEach(el => { el.checked = enabled })

    savePreferences(this.enabledIds)
    this._dispatch()
  }

  /** 切換個別規則的開關，同步更新類別 checkbox 狀態 */
  toggleRule(event) {
    const checkbox = event.target
    const ruleId = checkbox.dataset.ruleId
    const category = checkbox.dataset.category

    if (checkbox.checked) {
      this.enabledIds.add(ruleId)
    } else {
      this.enabledIds.delete(ruleId)
    }

    this._syncCategoryCheckbox(category)
    savePreferences(this.enabledIds)
    this._dispatch()
  }

  /** 括號轉換 radio button 互斥邏輯 */
  toggleParens(event) {
    const value = event.target.value

    this.enabledIds.delete('punct-half-to-full-parens')
    this.enabledIds.delete('punct-full-to-half-parens')

    if (value !== 'none') {
      this.enabledIds.add(value)
    }

    savePreferences(this.enabledIds)
    this._dispatch()
  }

  /** 重設所有開關為預設值，並清除 localStorage */
  resetDefaults() {
    this.enabledIds = getDefaultEnabledIds()
    clearPreferences()
    this._syncAllToggles()
    this._dispatch()
  }

  /** 手機版 sidebar 摺疊/展開 */
  toggleSidebar() {
    this.sidebarTarget.classList.toggle('hidden')
    this.sidebarArrowTarget.classList.toggle('rotate-180')
  }

  /** 展開/收合詞彙對照表 */
  toggleWordPairs() {
    this.wordPairsContainerTarget.classList.toggle('hidden')
    const arrow = this.wordPairsToggleTarget.querySelector('[data-arrow]')
    if (arrow) arrow.classList.toggle('rotate-180')
  }

  /** 展開/收合「其它已啟用修復」資訊區塊 */
  togglePostFix() {
    this.postFixContainerTarget.classList.toggle('hidden')
    const arrow = this.postFixToggleTarget.querySelector('[data-arrow]')
    if (arrow) arrow.classList.toggle('rotate-180')
  }

  // === Private ===

  /** dispatch custom event 通知外層 controller 偏好已變更 */
  _dispatch() {
    this.element.dispatchEvent(new CustomEvent('formatter-settings:changed', { bubbles: true }))
  }

  /** 依據 enabledIds 同步所有 toggle 的 UI 狀態 */
  _syncAllToggles() {
    // 類別 checkbox（排除括號規則）
    this.categoryToggleTargets.forEach(el => {
      const category = el.dataset.category
      const categoryRules = getRulesByCategory(category).filter(r => !PARENS_RULE_IDS.has(r.id))
      if (categoryRules.length === 0) return

      const enabledCount = categoryRules.filter(r => this.enabledIds.has(r.id)).length

      if (enabledCount === 0) {
        el.checked = false
        el.indeterminate = false
      } else if (enabledCount === categoryRules.length) {
        el.checked = true
        el.indeterminate = false
      } else {
        el.checked = false
        el.indeterminate = true
      }
    })

    // 個別規則 checkbox
    this.ruleToggleTargets.forEach(el => {
      el.checked = this.enabledIds.has(el.dataset.ruleId)
    })

    // 括號 radio
    const activeParens = this.enabledIds.has('punct-full-to-half-parens') ? 'punct-full-to-half-parens'
      : this.enabledIds.has('punct-half-to-full-parens') ? 'punct-half-to-full-parens'
        : 'none'
    this.parensRadioTargets.forEach(el => {
      el.checked = (el.value === activeParens)
    })
  }

  /** 根據子規則的勾選狀態更新類別 checkbox */
  _syncCategoryCheckbox(category) {
    const categoryCheckbox = this.categoryToggleTargets.find(el => el.dataset.category === category)
    if (!categoryCheckbox) return

    const categoryRules = getRulesByCategory(category).filter(r => !PARENS_RULE_IDS.has(r.id))
    if (categoryRules.length === 0) return

    const enabledCount = categoryRules.filter(r => this.enabledIds.has(r.id)).length

    if (enabledCount === 0) {
      categoryCheckbox.checked = false
      categoryCheckbox.indeterminate = false
    } else if (enabledCount === categoryRules.length) {
      categoryCheckbox.checked = true
      categoryCheckbox.indeterminate = false
    } else {
      categoryCheckbox.checked = false
      categoryCheckbox.indeterminate = true
    }
  }

  /** 從規則的 example 欄位讀取範例，注入對應的 DOM 元素 */
  _renderExamples() {
    this.ruleExampleTargets.forEach(el => {
      const ruleId = el.dataset.ruleId
      const rule = getRuleById(ruleId)
      if (rule?.example || rule?.name) {
        el.textContent = rule.example || rule.name
      }
    })
  }

  /** 從 CHINA_TO_TAIWAN_MAP 渲染詞彙對照表 */
  _renderWordPairs() {
    if (!this.hasWordPairsContainerTarget) return

    const pairs = getWordReplacementPairs()
    const container = this.wordPairsContainerTarget

    const list = document.createElement('div')
    list.className = 'flex flex-wrap gap-x-3 gap-y-0.5'

    pairs.forEach(([china, taiwan]) => {
      const item = document.createElement('span')
      item.className = 'text-gray-500 whitespace-nowrap'
      item.textContent = `${china}→${taiwan}`
      list.appendChild(item)
    })

    container.appendChild(list)
  }
}
